import { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import { useAppStore } from '../../state/useAppStore'
import { ZoomIn, ZoomOut, Home, ArrowLeft, Target } from 'lucide-react'

const DrilldownUAT = () => {
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  
  const { 
    selectedCounty,
    setDrilldownUAT,
    uatData,
    loadUatData,
    loading,
    error,
    mapZoom,
    setMapZoom,
    mapOffset,
    setMapOffset
  } = useAppStore()

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [hoveredUAT, setHoveredUAT] = useState(null)
  const [projection, setProjection] = useState(null)
  const [pathGenerator, setPathGenerator] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [countyUATs, setCountyUATs] = useState([])

  // Load UAT data if needed
  useEffect(() => {
    if (!uatData) {
      loadUatData()
    }
  }, [])

  // Filter UATs for selected county
  useEffect(() => {
    if (!uatData || !selectedCounty) return

    const filtered = uatData.features.filter(feature => {
      const countyName = feature.properties?.judet
      return countyName && countyName.toUpperCase() === selectedCounty.toUpperCase()
    })

    setCountyUATs(filtered)

    // Calculate bounding box for centering
    if (filtered.length > 0 && projection) {
      const bounds = d3.geoBounds({ type: 'FeatureCollection', features: filtered })
      const center = [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2]
      
      // Update projection center
      const newProjection = projection.copy().center(center)
      setProjection(newProjection)
    }
  }, [uatData, selectedCounty, projection])

  // Setup dimensions
  useEffect(() => {
    if (!containerRef.current) return

    const updateDimensions = () => {
      const { width, height } = containerRef.current.getBoundingClientRect()
      setDimensions({ width, height })
    }

    updateDimensions()
    window.addEventListener('resize', updateDimensions)

    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  // Initialize projection
  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return

    const proj = d3.geoMercator()
      .center([25, 46]) // Default center, will be updated
      .scale(Math.min(dimensions.width, dimensions.height) * 2)
      .translate([dimensions.width / 2 + mapOffset.x, dimensions.height / 2 + mapOffset.y])

    setProjection(proj)
    setPathGenerator(d3.geoPath().projection(proj))
  }, [dimensions, mapOffset])

  // Render UATs
  useEffect(() => {
    if (!svgRef.current || !countyUATs.length || !pathGenerator || !projection) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    // Update projection based on county bounds
    if (countyUATs.length > 0) {
      const bounds = d3.geoBounds({ type: 'FeatureCollection', features: countyUATs })
      const center = [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2]
      
      const width = bounds[1][0] - bounds[0][0]
      const height = bounds[1][1] - bounds[0][1]
      const scale = 0.9 / Math.max(width / dimensions.width, height / dimensions.height)
      
      projection.center(center).scale(scale)
    }

    // Draw UATs with extrusion effect
    svg.selectAll('path')
      .data(countyUATs)
      .enter()
      .append('path')
      .attr('d', pathGenerator)
      .attr('class', 'uat-path')
      .attr('data-uat', d => d.properties?.name || 'Unknown')
      .style('fill', d => {
        const coverage = Math.random() // Simulated coverage data
        if (coverage > 0.7) return 'url(#high-coverage)'
        if (coverage > 0.3) return 'url(#medium-coverage)'
        return 'url(#low-coverage)'
      })
      .style('stroke', d => {
        const coverage = Math.random()
        if (coverage > 0.7) return 'rgba(0, 247, 255, 0.8)'
        if (coverage > 0.3) return 'rgba(157, 0, 255, 0.8)'
        return 'rgba(255, 0, 255, 0.8)'
      })
      .style('stroke-width', 1)
      .style('cursor', 'pointer')
      .style('opacity', 0.9)
      .style('filter', 'url(#extrude)')
      .on('mouseover', function(event, d) {
        const uatName = d.properties?.name || 'Unknown'
        setHoveredUAT(uatName)
        
        d3.select(this)
          .style('filter', 'url(#glow)')
          .style('stroke-width', 2)

        // Show tooltip
        if (tooltipRef.current) {
          const coverage = Math.random() * 100
          const population = Math.floor(Math.random() * 50000) + 1000
          
          tooltipRef.current.style.opacity = 1
          tooltipRef.current.style.left = `${event.pageX + 10}px`
          tooltipRef.current.style.top = `${event.pageY - 10}px`
          tooltipRef.current.innerHTML = `
            <div class="font-orbitron font-bold text-monster-cyan">${uatName}</div>
            <div class="text-sm">${selectedCounty} County</div>
            <div class="text-xs mt-2">
              <div>Coverage: ${coverage.toFixed(1)}%</div>
              <div>Population: ${population.toLocaleString()}</div>
              <div>Type: ${d.properties?.type || 'UAT'}</div>
            </div>
          `
        }
      })
      .on('mouseout', function() {
        setHoveredUAT(null)
        d3.select(this)
          .style('filter', 'url(#extrude)')
          .style('stroke-width', 1)

        if (tooltipRef.current) {
          tooltipRef.current.style.opacity = 0
        }
      })
      .on('click', (event, d) => {
        console.log('UAT clicked:', d.properties)
        // Add custom click behavior here
      })

    // Add gradient definitions
    const defs = svg.append('defs')

    // Coverage gradients
    const gradients = [
      { id: 'high-coverage', colors: ['rgba(0, 247, 255, 0.6)', 'rgba(0, 247, 255, 0.3)'] },
      { id: 'medium-coverage', colors: ['rgba(157, 0, 255, 0.6)', 'rgba(157, 0, 255, 0.3)'] },
      { id: 'low-coverage', colors: ['rgba(255, 0, 255, 0.6)', 'rgba(255, 0, 255, 0.3)'] }
    ]

    gradients.forEach(gradient => {
      defs.append('linearGradient')
        .attr('id', gradient.id)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '100%')
        .selectAll('stop')
        .data([
          { offset: '0%', color: gradient.colors[0] },
          { offset: '100%', color: gradient.colors[1] }
        ])
        .enter()
        .append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color)
    })

    // Extrusion filter (shadow effect)
    const extrudeFilter = defs.append('filter')
      .attr('id', 'extrude')
      .attr('x', '-20%')
      .attr('y', '-20%')
      .attr('width', '140%')
      .attr('height', '140%')

    extrudeFilter.append('feDropShadow')
      .attr('dx', 2)
      .attr('dy', 2)
      .attr('stdDeviation', 3)
      .attr('flood-color', 'rgba(0, 247, 255, 0.3)')

    // Glow filter
    const glowFilter = defs.append('filter')
      .attr('id', 'glow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%')

    glowFilter.append('feGaussianBlur')
      .attr('stdDeviation', '4')
      .attr('result', 'coloredBlur')

    const feMerge = glowFilter.append('feMerge')
    feMerge.append('feMergeNode').attr('in', 'coloredBlur')
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic')

  }, [countyUATs, pathGenerator, projection, selectedCounty])

  // Mouse handlers
  const handleMouseDown = (event) => {
    setIsDragging(true)
    setDragStart({ x: event.clientX - mapOffset.x, y: event.clientY - mapOffset.y })
  }

  const handleMouseMove = (event) => {
    if (!isDragging) return
    setMapOffset({
      x: event.clientX - dragStart.x,
      y: event.clientY - dragStart.y
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleZoom = (direction) => {
    const newZoom = direction === 'in' ? mapZoom * 1.2 : mapZoom / 1.2
    setMapZoom(Math.min(Math.max(newZoom, 0.5), 5))
  }

  const handleBack = () => {
    setDrilldownUAT(false)
    setMapOffset({ x: 0, y: 0 })
    setMapZoom(1)
  }

  // Handle wheel zoom
  useEffect(() => {
    const handleWheel = (event) => {
      event.preventDefault()
      const direction = event.deltaY > 0 ? 'out' : 'in'
      handleZoom(direction)
    }

    const container = containerRef.current
    container?.addEventListener('wheel', handleWheel, { passive: false })

    return () => container?.removeEventListener('wheel', handleWheel)
  }, [mapZoom])

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-monster-magenta border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-monster-magenta font-orbitron">Loading UAT Data...</p>
          <p className="text-sm text-gray-400">Analyzing {selectedCounty} localities</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center p-8 monster-card max-w-md">
          <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <div className="w-6 h-6 text-red-400">⚠️</div>
          </div>
          <h3 className="text-xl font-orbitron mb-2">UAT Data Error</h3>
          <p className="text-gray-400 mb-4">{error}</p>
          <button 
            onClick={loadUatData}
            className="monster-btn"
          >
            Retry Loading
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-50 pointer-events-none opacity-0 transition-opacity duration-200"
        style={{
          background: 'rgba(10, 10, 26, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0, 247, 255, 0.3)',
          borderRadius: '12px',
          padding: '12px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
          maxWidth: '250px'
        }}
      />

      {/* Back Button */}
      <div className="absolute top-6 left-6 z-10">
        <button
          onClick={handleBack}
          className="px-6 py-3 rounded-xl bg-monster-card/80 backdrop-blur-xs border border-monster-border hover:bg-monster-card transition-all duration-300 flex items-center gap-2 group"
        >
          <ArrowLeft className="w-4 h-4 text-monster-cyan" />
          <span className="font-orbitron">Back to National</span>
        </button>
      </div>

      {/* Map Container */}
      <div 
        ref={containerRef}
        className="relative w-full h-full overflow-hidden rounded-2xl monster-card"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        {/* Header Overlay */}
        <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-10">
          <div className="px-6 py-3 rounded-xl bg-monster-card/80 backdrop-blur-xs border border-monster-border">
            <h2 className="text-xl font-orbitron text-center">
              {selectedCounty} • <span className="text-monster-cyan">{countyUATs.length} UATs</span>
            </h2>
            <p className="text-sm text-gray-400 text-center">Localities Analysis</p>
          </div>
        </div>

        {/* SVG Map */}
        <svg
          ref={svgRef}
          width={dimensions.width}
          height={dimensions.height}
          style={{
            transform: `scale(${mapZoom})`,
            transformOrigin: 'center'
          }}
        />

        {/* Map Controls */}
        <div className="absolute bottom-6 right-6 flex flex-col gap-3">
          <button
            onClick={() => handleZoom('in')}
            className="p-3 rounded-xl bg-monster-card/80 backdrop-blur-xs border border-monster-border hover:bg-monster-card transition-all duration-300"
          >
            <ZoomIn className="w-5 h-5 text-monster-cyan" />
          </button>
          <button
            onClick={() => handleZoom('out')}
            className="p-3 rounded-xl bg-monster-card/80 backdrop-blur-xs border border-monster-border hover:bg-monster-card transition-all duration-300"
          >
            <ZoomOut className="w-5 h-5 text-monster-cyan" />
          </button>
          <button
            onClick={() => setMapOffset({ x: 0, y: 0 })}
            className="p-3 rounded-xl bg-monster-card/80 backdrop-blur-xs border border-monster-border hover:bg-monster-card transition-all duration-300"
          >
            <Home className="w-5 h-5 text-monster-cyan" />
          </button>
        </div>

        {/* Legend */}
        <div className="absolute bottom-6 left-6">
          <div className="px-4 py-3 rounded-xl bg-monster-card/80 backdrop-blur-xs border border-monster-border">
            <div className="text-sm font-orbitron mb-2">COVERAGE</div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-monster-cyan" />
                <span className="text-xs">High (&gt;70%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-monster-magenta" />
                <span className="text-xs">Medium (30-70%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-monster-violet" />
                <span className="text-xs">Low (&lt;30%)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Overlay */}
        <div className="absolute top-20 right-6">
          <div className="px-4 py-3 rounded-xl bg-monster-card/80 backdrop-blur-xs border border-monster-border">
            <div className="text-sm font-orbitron mb-2">UAT STATS</div>
            <div className="space-y-2">
              <div className="text-xs">
                <div className="text-gray-400">Total UATs:</div>
                <div className="text-monster-cyan font-bold">{countyUATs.length}</div>
              </div>
              <div className="text-xs">
                <div className="text-gray-400">Avg Population:</div>
                <div className="text-monster-cyan font-bold">
                  {Math.floor(countyUATs.length * 15000).toLocaleString()}
                </div>
              </div>
              <div className="text-xs">
                <div className="text-gray-400">Coverage Score:</div>
                <div className="text-monster-cyan font-bold">
                  {(Math.random() * 40 + 60).toFixed(1)}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-monster-dark/80 backdrop-blur-sm flex items-center justify-center">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-monster-magenta border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-monster-magenta font-orbitron">Rendering UAT Map</p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default DrilldownUAT